(() => {
  'use strict';

  const highVariantSelector = '.ev-product-form[data-high-variant-picker="true"]';

  const selectedButtons = (form) =>
    Array.from(form.querySelectorAll('[data-option-picker]')).map((picker) =>
      picker.querySelector('[data-option-picker-option].is-selected')
    );

  const closeAllPickers = (form) => {
    form.querySelectorAll('[data-option-picker].is-open').forEach((picker) => {
      picker.classList.remove('is-open');
      picker.querySelector('[data-option-picker-button]')?.setAttribute('aria-expanded', 'false');
    });
  };

  const dispatchVariantChange = (form) => {
    const variantInput = form.querySelector('[data-variant-select]');
    if (!variantInput) return;

    const pickers = Array.from(form.querySelectorAll('[data-option-picker]'));
    const selectedValues = selectedButtons(form).map((button) => button?.dataset.value || '');
    const featuredMediaIndex = Number(variantInput.dataset.featuredMediaIndex || -1);

    form.dispatchEvent(new CustomEvent('ev:variant:change', {
      bubbles: true,
      detail: {
        variantId: variantInput.value,
        selectedValues,
        optionNames: pickers.map((picker) => picker.dataset.optionName || ''),
        featuredMediaIndex
      }
    }));
  };

  const copyVariantState = (currentInput, nextInput) => {
    currentInput.value = nextInput.value;
    ['data-available', 'data-options', 'data-featured-media-index'].forEach((name) => {
      if (nextInput.hasAttribute(name)) currentInput.setAttribute(name, nextInput.getAttribute(name));
      else currentInput.removeAttribute(name);
    });
  };

  const replaceRenderedState = (form, responseText) => {
    const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
    const nextForm = documentFragment.getElementById(form.id);
    const currentSelectors = form.querySelector('[data-option-selectors]');
    const nextSelectors = nextForm?.querySelector('[data-option-selectors]');
    const currentInput = form.querySelector('[data-variant-select]');
    const nextInput = nextForm?.querySelector('[data-variant-select]');

    if (!nextForm || !currentSelectors || !nextSelectors || !currentInput || !nextInput) {
      throw new Error('High-variant section response did not contain the expected picker state.');
    }

    currentSelectors.replaceWith(nextSelectors);
    copyVariantState(currentInput, nextInput);

    const priceBox = form.closest('.ev-product__info')?.querySelector('[data-variant-price]');
    const nextPriceBox = documentFragment.querySelector('[data-variant-price]');
    if (priceBox && nextPriceBox) priceBox.innerHTML = nextPriceBox.innerHTML;

    const addButton = form.querySelector('[data-add-button]');
    const nextAddButton = nextForm.querySelector('[data-add-button]');
    if (addButton && nextAddButton) {
      addButton.disabled = nextAddButton.disabled;
      addButton.textContent = nextAddButton.textContent;
      ['data-add-text', 'data-sold-out-text'].forEach((name) => {
        if (nextAddButton.hasAttribute(name)) addButton.setAttribute(name, nextAddButton.getAttribute(name));
      });
    }

    dispatchVariantChange(form);
  };

  const requestSelection = async (form, optionButton) => {
    if (optionButton.disabled || form.dataset.highVariantLoading === 'true') return;

    const picker = optionButton.closest('[data-option-picker]');
    if (!picker) return;

    const selectedIds = Array.from(form.querySelectorAll('[data-option-picker]'))
      .map((candidatePicker) => {
        const selected = candidatePicker === picker
          ? optionButton
          : candidatePicker.querySelector('[data-option-picker-option].is-selected');
        return selected?.dataset.optionValueId || '';
      })
      .filter(Boolean);

    if (!selectedIds.length) return;

    const sectionId = form.dataset.sectionId;
    const basePath = optionButton.dataset.optionProductUrl || form.dataset.productUrl || window.location.pathname;
    const requestUrl = new URL(basePath, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    requestUrl.searchParams.set('option_values', selectedIds.join(','));

    form.dataset.highVariantLoading = 'true';
    form.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`High-variant section request failed: ${response.status}`);
      replaceRenderedState(form, await response.text());

      const pageUrl = new URL(basePath, window.location.origin);
      pageUrl.searchParams.set('option_values', selectedIds.join(','));
      pageUrl.searchParams.delete('variant');
      window.history.replaceState({}, '', `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`);
    } catch (error) {
      console.error(error);
    } finally {
      delete form.dataset.highVariantLoading;
      form.removeAttribute('aria-busy');
    }
  };

  const init = (form) => {
    if (form.dataset.highVariantPickerReady === 'true') return;
    form.dataset.highVariantPickerReady = 'true';

    form.addEventListener('click', (event) => {
      const optionButton = event.target.closest('[data-option-picker-option]');
      if (optionButton && form.contains(optionButton)) {
        event.preventDefault();
        requestSelection(form, optionButton);
        return;
      }

      const pickerButton = event.target.closest('[data-option-picker-button]');
      if (!pickerButton || !form.contains(pickerButton)) return;

      event.preventDefault();
      const picker = pickerButton.closest('[data-option-picker]');
      if (!picker) return;
      const isOpen = picker.classList.contains('is-open');
      closeAllPickers(form);
      if (!isOpen) {
        picker.classList.add('is-open');
        pickerButton.setAttribute('aria-expanded', 'true');
      }
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) closeAllPickers(form);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openPicker = form.querySelector('[data-option-picker].is-open');
      if (!openPicker) return;
      closeAllPickers(form);
      openPicker.querySelector('[data-option-picker-button]')?.focus({ preventScroll: true });
    });

    dispatchVariantChange(form);
  };

  const initAll = () => document.querySelectorAll(highVariantSelector).forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();